import { z } from 'zod';

export const timeSlotTypeSchema = z.enum(['daily', 'weekly', 'monthly']);

export type TimeSlotType = z.infer<typeof timeSlotTypeSchema>;

export const operationsDashboardInput = z.object({
  timeSlotType: timeSlotTypeSchema.default('daily'),
  categories: z.array(z.string()).optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

export type OperationsDashboardInput = z.infer<typeof operationsDashboardInput>;

const timeSlotServicesSummarySchema: z.ZodType<TimeSlotServicesSummary> =
  z.lazy(() =>
    z.object({
      timeSlot: z.string(),
      totalServices: z.number(),
      servicesByCategory: z.record(z.string(), z.number()),
      servicesByDay: z.record(z.string(), z.number()).optional(),
      children: z.array(timeSlotServicesSummarySchema).optional(),
    })
  );

export type TimeSlotServicesSummary = {
  timeSlot: string;
  totalServices: number;
  servicesByCategory: Record<string, number>;
  servicesByDay?: Record<string, number>;
  children?: TimeSlotServicesSummary[];
};

export const serviceCategoryFilterSchema = z.object({
  category: z.string(),
  count: z.number(),
});

export type ServiceCategoryFilter = z.infer<typeof serviceCategoryFilterSchema>;

const showUpByStatusSchema = z.object({
  confirmed: z.number(),
  awaiting: z.number(),
  rejected: z.number(),
});

const assignmentsSchema = z.object({
  assigned: z.number(),
  unassigned: z.number(),
});

const ticketStatsSchema = z.object({
  assigned: z.number(),
  missing: z.number(),
});

export const topStatsSchema = z.object({
  guides: z.object({
    today: showUpByStatusSchema,
    tomorrow: showUpByStatusSchema,
    assignments: z.object({
      today: assignmentsSchema,
      tomorrow: assignmentsSchema,
    }),
  }),
  drivers: z.object({
    today: showUpByStatusSchema,
    tomorrow: showUpByStatusSchema,
    assignments: z.object({
      today: assignmentsSchema,
      tomorrow: assignmentsSchema,
    }),
  }),
  tickets: z.object({
    thisWeek: ticketStatsSchema,
    nextWeek: ticketStatsSchema,
  }),
});

export type TopStats = z.infer<typeof topStatsSchema>;

export const operationsDashboardResponseSchema = z.object({
  timeslots: z.array(timeSlotServicesSummarySchema),
  availableCategories: z.array(serviceCategoryFilterSchema),
  colorPalette: z.record(z.string(), z.string()),
  totalUpcomingServices: z.number(),
  totalConfirmedServices: z.number(),
  totalAwaitingServices: z.number(),
  totalRejectedServices: z.number(),
  totalAssignedServices: z.number(),
  totalUnassignedServices: z.number(),
  topStats: topStatsSchema,
  planned: z.object({
    guides: z.array(timeSlotServicesSummarySchema),
    transport: z.array(timeSlotServicesSummarySchema),
    tickets: z.array(timeSlotServicesSummarySchema),
  }),
});

export type OperationsDashboardResponse = z.infer<
  typeof operationsDashboardResponseSchema
>;

// Red Flag card — red-flagged itineraries: the active version carries a non-zero
// attention_category rollup OR a live incident covers the trip (XT-625; the rule
// itself lives in `red-flag-linkage.ts`). All filters are optional; the card is
// org-scoped server-side.
export const redFlagsInput = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  status: z.array(z.string()).optional(),
});

export type RedFlagsInput = z.infer<typeof redFlagsInput>;

export const redFlagItemSchema = z.object({
  id: z.string(),
  title: z.string(),
});

export type RedFlagItem = z.infer<typeof redFlagItemSchema>;

export const redFlagsSchema = z.object({
  // Exact, and independent of `items.length` — the card renders a collapsed
  // preview, so the row list is capped server-side while the total is not.
  totalCount: z.number(),
  items: z.array(redFlagItemSchema),
});

export type RedFlags = z.infer<typeof redFlagsSchema>;
